import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import { fetchSalesOrders } from './salesOrders';

export interface ConsultationFinding {
  consultation_findings_id: number;
  consultation_type_description: string;
  consultation_findings_name: string;
  consultation_findings_description: string;
  service_id: number;
}

export interface ConsultationFindingsState {
  status: string;
  editingStatus: string;
  consultationFindings: ConsultationFinding[];
}

const initialState: ConsultationFindingsState = {
  status: 'idle',
  editingStatus: 'idle',
  consultationFindings: [],
};

export const fetchConsultationFindings = createAsyncThunk(
  'consultationFindings/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const consultationFindingsResponse: any = await client.get(
        '/healthpoint/consultation-findings'
      );
      return consultationFindingsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

type EditConsultationFinding = {
  consultationsFindingDescription: string;
  consultationFindingId: number;
};

export const editConsultationFinding = createAsyncThunk(
  'consultationFinding/edit',
  async (editConsultationFinding: EditConsultationFinding, { dispatch, rejectWithValue }) => {
    try {
      await client.put(
        `/healthpoint/consultation-finding/${editConsultationFinding.consultationFindingId}`,
        {
          consultationsFindingDescription: editConsultationFinding.consultationsFindingDescription,
        }
      );
      dispatch(fetchConsultationFindings());
      dispatch(fetchSalesOrders());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const consultationFindingsSlice = createSlice({
  name: 'consultationFindings',
  initialState,
  reducers: {
    resetConsultationFindings: (_) => {
      return initialState;
    },
    resetDeletingStatus: (state) => {
      return {
        ...state,
        deletingStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsultationFindings.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchConsultationFindings.fulfilled, (state, action) => {
        state.consultationFindings = action.payload.data.consultationFindings;
        state.status = 'fulfilled';
      })
      .addCase(fetchConsultationFindings.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(editConsultationFinding.pending, (state) => {
        state.editingStatus = 'pending';
      })
      .addCase(editConsultationFinding.fulfilled, (state) => {
        state.editingStatus = 'fulfilled';
        toast.success('Successfully updated the findings.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(editConsultationFinding.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.editingStatus = 'rejected';
      });
  },
});

export const { resetConsultationFindings, resetDeletingStatus } = consultationFindingsSlice.actions;

export default consultationFindingsSlice.reducer;

export const selectConsultationFindings = (state: RootState) =>
  state.consultationFindings.consultationFindings;
export const selectConsultationFindingStatus = (state: RootState) =>
  state.consultationFindings.status;
export const selectConsultationFindingEditingStatus = (state: RootState) =>
  state.consultationFindings.editingStatus;
